<script setup lang="ts">
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import logo from '../../assets/home/logo.webp';
import LanguageSwitcher from '../ui/LanguageSwitcher.vue';

const { t } = useI18n();

interface FooterLink { label: string; to?: string; href?: string }
// `key` is the stable accordion identity (locale-independent); `title` is shown.
const columns = computed<{ key: string; title: string; links: FooterLink[] }[]>(() => [
  { key: 'SHOP', title: t('footer.shop'), links: [{ label: t('footer.marketplace'), to: '/catalog' }, { label: t('footer.catalog'), to: '/catalog' }, { label: t('footer.articles'), to: '/articles' }] },
  { key: 'LEGAL', title: t('footer.legal'), links: [{ label: t('footer.terms'), to: '/terms' }, { label: t('footer.privacy'), href: '#' }, { label: t('footer.cookies'), to: '/cookies' }] },
  { key: 'HELP', title: t('footer.help'), links: [{ label: t('footer.support'), href: 'mailto:support@playstock.red' }, { label: t('footer.faq'), to: '/faq' }] },
]);

// Mobile accordion — SHOP open by default.
const open = ref<Set<string>>(new Set(['SHOP']));
function toggle(title: string) {
  const next = new Set(open.value);
  next.has(title) ? next.delete(title) : next.add(title);
  open.value = next;
}
</script>

<template>
  <footer class="relative z-10">
    <div class="mx-auto max-w-[1200px] px-5 py-14">
      <!-- Desktop: brand + columns -->
      <div class="hidden gap-10 md:grid md:grid-cols-[1.6fr_repeat(3,1fr)_auto]">
        <div>
          <img :src="logo" alt="PlayStock" class="mb-4 h-10 w-auto" />
          <p class="max-w-xs text-sm leading-relaxed text-muted">{{ t('footer.tagline') }}</p>
        </div>
        <div v-for="col in columns" :key="col.key">
          <h4 class="mb-4 font-display text-xs tracking-widest text-accent">{{ col.title }}</h4>
          <ul class="space-y-3">
            <li v-for="link in col.links" :key="link.label">
              <component :is="link.to ? 'router-link' : 'a'" :to="link.to" :href="link.href" class="cursor-pointer text-sm text-muted transition hover:text-accent">{{ link.label }}</component>
            </li>
          </ul>
        </div>
        <div>
          <h4 class="mb-4 font-display text-xs tracking-widest text-accent">{{ t('footer.language') }}</h4>
          <LanguageSwitcher />
        </div>
      </div>

      <!-- Mobile: brand + accordions -->
      <div class="md:hidden">
        <img :src="logo" alt="PlayStock" class="mb-4 h-10 w-auto" />
        <p class="mb-8 max-w-xs text-sm leading-relaxed text-muted">{{ t('footer.tagline') }}</p>
        <div class="divide-y divide-line border-y border-line">
          <div v-for="col in columns" :key="col.key">
            <button class="flex w-full cursor-pointer items-center justify-between py-4" @click="toggle(col.key)">
              <span class="font-display text-sm tracking-widest text-accent">{{ col.title }}</span>
              <svg width="14" height="14" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" class="text-muted">
                <path v-if="open.has(col.key)" d="M5 12h14" />
                <path v-else d="M12 5v14M5 12h14" />
              </svg>
            </button>
            <ul v-if="open.has(col.key)" class="space-y-3 pb-4">
              <li v-for="link in col.links" :key="link.label">
                <component :is="link.to ? 'router-link' : 'a'" :to="link.to" :href="link.href" class="cursor-pointer text-sm text-muted transition hover:text-accent">{{ link.label }}</component>
              </li>
            </ul>
          </div>
          <div class="flex items-center justify-between py-4">
            <span class="font-display text-sm tracking-widest text-accent">{{ t('footer.language') }}</span>
            <LanguageSwitcher align="right" />
          </div>
        </div>
      </div>

      <p class="mt-10 border-t border-line pt-6 text-xs leading-relaxed text-muted md:mt-12">
        {{ t('footer.legalLine') }}
        <br />
        {{ t('footer.copyright') }}
      </p>
    </div>
  </footer>
</template>
